import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Loader2 } from 'lucide-react';
import {
  AdminOrganizationDetail,
  UpdateAdminOrganizationPayload
} from '@utils/types/admin';
import { useUpdateAdminOrganization } from '@utils/api/admin/use-update-admin-organization';
import Input from '@components/atoms/ui-kit/input';
import Button from '@components/atoms/ui-kit/button';
import toast from 'react-hot-toast';

type OrganizationProfileEditProps = {
  organization: AdminOrganizationDetail;
  onCancel: () => void;
  onSaved: () => void;
};

/**
 * Inline edit form for organization profile
 */
export default function OrganizationProfileEdit({
  organization,
  onCancel,
  onSaved
}: OrganizationProfileEditProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const updateMutation = useUpdateAdminOrganization();

  const [name, setName] = useState(organization.name ?? '');
  const [email, setEmail] = useState(organization.email ?? '');
  const [isActive, setIsActive] = useState(organization.is_active ?? true);
  const [retentionDays, setRetentionDays] = useState(
    organization.retention_days?.toString() ?? '90'
  );
  const [maxUsers, setMaxUsers] = useState(
    organization.max_users?.toString() ?? '3'
  );
  const [batchPriority, setBatchPriority] = useState(
    organization.batch_priority?.toString() ?? '200'
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = t('name-is-required');
    if (!email.trim()) {
      newErrors.email = t('email-is-required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('invalid-email');
    }
    if (retentionDays && isNaN(Number(retentionDays)))
      newErrors.retentionDays = t('invalid-number');
    if (maxUsers && isNaN(Number(maxUsers)))
      newErrors.maxUsers = t('invalid-number');
    if (batchPriority && isNaN(Number(batchPriority)))
      newErrors.batchPriority = t('invalid-number');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload: UpdateAdminOrganizationPayload = {
      name: name.trim(),
      email: email.trim(),
      is_active: isActive,
      retention_days: retentionDays ? Number(retentionDays) : undefined,
      max_users: maxUsers ? Number(maxUsers) : undefined,
      batch_priority: batchPriority ? Number(batchPriority) : undefined
    };

    updateMutation.mutate(
      { id: organization.id, payload },
      {
        onSuccess: () => {
          onSaved();
        },
        onError: (error: unknown) => {
          const err = error as {
            response?: {
              data?: {
                error_description?: string;
                detail?: string;
                message?: string;
              };
            };
          };
          const msg =
            err?.response?.data?.error_description ||
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            t('an-error-occurred');
          toast.error(msg);
        }
      }
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          id="org-name"
          label={t('name')}
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={t('enter-organization-name')}
          error={errors.name}
          required
          size="small"
        />
        <Input
          id="org-email"
          label={t('email')}
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={t('enter-email')}
          error={errors.email}
          required
          size="small"
        />
      </div>

      <div
        className="rounded-2xl border p-4"
        style={{
          borderColor: getColour('border'),
          backgroundColor: getColour('thirdLayer')
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <label
              className="text-sm font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('active')}
            </label>
            <p className="text-xs" style={{ color: getColour('inactiveText') }}>
              {t('organization-active-description')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsActive(!isActive)}
            className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
            style={{
              backgroundColor: isActive
                ? getColour('activeText')
                : getColour('border')
            }}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'}`}
              style={{ backgroundColor: getColour('baseLayer') }}
            />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          id="retention-days"
          label={t('retention-days')}
          type="number"
          numeric
          value={retentionDays}
          onChange={e => setRetentionDays(e.target.value)}
          placeholder="90"
          error={errors.retentionDays}
          size="small"
        />
        <Input
          id="max-users"
          label={t('max-users')}
          type="number"
          numeric
          value={maxUsers}
          onChange={e => setMaxUsers(e.target.value)}
          placeholder="3"
          error={errors.maxUsers}
          size="small"
        />
        <Input
          id="batch-priority"
          label={t('batch-priority')}
          type="number"
          numeric
          value={batchPriority}
          onChange={e => setBatchPriority(e.target.value)}
          placeholder="200"
          error={errors.batchPriority}
          size="small"
        />
      </div>

      <div className="flex items-center justify-end gap-3 pt-4">
        <Button
          onClick={onCancel}
          disabled={updateMutation.isLoading}
          label={t('cancel')}
          variant="smoke"
          size="small"
        />
        <Button
          onClick={handleSubmit}
          disabled={updateMutation.isLoading}
          label={updateMutation.isLoading ? t('saving') : t('save-changes')}
          variant="primary"
          size="small"
          icon={
            updateMutation.isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : undefined
          }
        />
      </div>
    </div>
  );
}
